import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiAlertTriangle } from "react-icons/fi";
import { SingleValue } from "react-select";
import CreatableSelect from "react-select/creatable";
import api from "../../api";
import { Location } from "../../types";
import { parseAddress } from "../../utils";

export interface AddRouteFormData {
  routeName: string;
  startLocation: Location | null;
  endLocation: Location | null;
}

interface AddRouteProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (formData: AddRouteFormData) => void;
}

interface OptionType {
  value: Location;
  label: string;
}

/**
 * Validates an address using the Nominatim API.
 * Returns the first result (if any) or null if not found.
 */
const validateAddress = async (address: string) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
    address
  )}`;
  try {
    const response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "User-Agent": "Capstone/1.0",
      },
    });
    if (!response.ok) {
      console.error("Error contacting geocoder");
      return null;
    }
    const results = await response.json();
    if (results && results.length > 0) {
      return results[0]; // return the first result
    } else {
      return null;
    }
  } catch (error) {
    console.error("Geocoding error:", error);
    return null;
  }
};

const AddRoute: React.FC<AddRouteProps> = ({ isVisible, onClose, onSave }) => {
  const [routeName, setRouteName] = useState<string>("");
  const [startLocation, setStartLocation] = useState<Location | null>(null);
  const [endLocation, setEndLocation] = useState<Location | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [retry, setRetry] = useState(0);
  const [errors, setErrors] = useState<{
    routeName?: string;
    startLocation?: string;
    endLocation?: string;
  }>({});
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    const fetchAllRoutes = async () => {
      try {
        const { data: response } = await api.get("/route/fetch-locations");
        setLocations(response);
      } catch (error) {
        console.error("Failed to fetch routes:", error);
      }
    };

    fetchAllRoutes();
  }, [token, retry]);

  // When an option is selected normally
  const handleStartLocationChange = (selected: SingleValue<OptionType>) => {
    setStartLocation(selected ? selected.value : null);
  };
  const handleEndLocationChange = (selected: SingleValue<OptionType>) => {
    setEndLocation(selected ? selected.value : null);
  };

  // When a custom address is created for the supplier (start location)
  const handleCreateSupplierOption = async (inputValue: string) => {
    const geocoded = await validateAddress(inputValue);
    if (geocoded) {
      const { state, city, postalCode } = parseAddress(geocoded.display_name);
      // Create a temporary location object (id=0 as a placeholder)
      const newOption: Location = {
        id: 0,
        city,
        state,
        postalCode,
        lat: geocoded.lat,
        lon: geocoded.lon,
        address: geocoded.display_name,
      };

      // Add the temporary option to the locations list
      setLocations((prev) => [...prev, newOption]);
      setErrors((prev) => ({ ...prev, startLocation: undefined }));

      const { data: response } = await api.post("/route/add-location", {
        city: newOption.city,
        state: newOption.state,
        postalCode: newOption.postalCode,
        lat: newOption.lat,
        lon: newOption.lon,
        address: newOption.address,
      });

      if (!response.ok) {
        throw new Error(`Error adding location: ${response.status}`);
      }
      // Capture the created location (which includes its id)
      const createdLocation: Location = await response.json();
      // Update the locations list to replace the temporary location with the created one
      setLocations((prev) =>
        prev.map((loc) =>
          loc.address === newOption.address ? createdLocation : loc
        )
      );
      // Set the start location to the newly created location
      setStartLocation(createdLocation);
    } else {
      setErrors((prev) => ({
        ...prev,
        startLocation: "Address not found. Please enter a valid address.",
      }));
    }
  };

  // When a custom address is created for the Walmart destination (end location)
  const handleCreateWalmartOption = async (inputValue: string) => {
    const geocoded = await validateAddress(inputValue);
    if (geocoded) {
      const { state, city, postalCode } = parseAddress(geocoded.display_name);
      const newOption: Location = {
        id: 0,
        city,
        state,
        postalCode,
        lat: geocoded.lat,
        lon: geocoded.lon,
        address: geocoded.display_name,
      };

      setLocations((prev) => [...prev, newOption]);
      setErrors((prev) => ({ ...prev, endLocation: undefined }));

      const { data: response } = await api.post("/route/add-location", {
        city: newOption.city,
        state: newOption.state,
        postalCode: newOption.postalCode,
        lat: newOption.lat,
        lon: newOption.lon,
        address: newOption.address,
      });

      if (!response.ok) {
        throw new Error(`Error adding location: ${response.status}`);
      }
      const createdLocation: Location = await response.json();
      setLocations((prev) =>
        prev.map((loc) =>
          loc.address === newOption.address ? createdLocation : loc
        )
      );
      setEndLocation(createdLocation);
    } else {
      setErrors((prev) => ({
        ...prev,
        endLocation: "Address not found. Please enter a valid address.",
      }));
    }
  };

  const handleSave = async () => {
    const newErrors: {
      routeName?: string;
      startLocation?: string;
      endLocation?: string;
    } = {};

    if (!routeName) {
      newErrors.routeName = "Route name is required";
    }
    if (!startLocation) {
      newErrors.startLocation =
        "Select a supplier location or add a custom one";
    }
    if (!endLocation) {
      newErrors.endLocation = "Select a Walmart location or add a custom one";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    // Call the onSave callback with the new form data
    onSave({
      routeName,
      startLocation,
      endLocation,
    });
    setRetry((prev) => prev + 1);
    const startLocId = locations.filter(
      (loc) => loc.address === startLocation?.address
    )[0].id;
    const endLocId = locations.filter(
      (loc) => loc.address === endLocation?.address
    )[0].id;

    // --- API Call to Save Route ---
    const apiEndpoint = "https://api.seth-towe.com/route/add-route";
    const token = localStorage.getItem("accessToken");
    if (!token) {
      console.error("No access token found.");
      return;
    }
    const requestBody = {
      name: routeName,
      startLocId: startLocId.toString(),
      endLocId: endLocId.toString(),
    };

    try {
      await toast.promise(
        (async () => {
          const response = await fetch(apiEndpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
          });
          if (!response.ok) {
            throw new Error("Failed to add route");
          }
          const data = await response.json();
          return data;
        })(),
        {
          loading: "Saving route...",
          success: "Route added successfully!",
          error: "Failed to add route",
        }
      );
    } catch (error) {
      console.error("Error sending request:", error);
    }

    // Reset fields
    setRouteName("");
    setStartLocation(null);
    setEndLocation(null);
    onClose();
  };

  if (!isVisible) return null;

  // Helper component for error messages
  const ErrorMessage: React.FC<{ message?: string }> = ({ message }) => {
    if (!message) return null;
    return (
      <div className="text-red-600 text-sm flex items-center mt-1">
        <FiAlertTriangle className="mr-1" />
        <span>{message}</span>
      </div>
    );
  };

  // CSS helpers
  const inputBorderClass = (fieldError?: string) =>
    fieldError ? "border-red-500" : "border-gray-300";

  const selectStyles = (hasError: boolean) => ({
    control: (provided: any) => ({
      ...provided,
      borderColor: hasError ? "#f56565" : "#d2d6dc",
    }),
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">Shipment And Route Details</h2>

        {/* Route Name Field */}
        <div className="mt-4">
          <label className="block mb-1 font-medium text-gray-700">
            Route Name
          </label>
          <input
            type="text"
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            placeholder="Enter route name"
            className={`w-full p-2 rounded focus:outline-none border ${inputBorderClass(
              errors.routeName
            )}`}
          />
          <ErrorMessage message={errors.routeName} />
        </div>

        {/* Start Location (Supplier) */}
        <div className="mt-4">
          <label className="block mb-1 font-medium text-gray-700">
            Start (Supplier)*
          </label>
          <CreatableSelect
            options={locations.map((loc) => ({
              value: loc,
              label: `${loc.city}, ${loc.state}`,
            }))}
            onChange={handleStartLocationChange}
            onCreateOption={(inputValue: string) =>
              handleCreateSupplierOption(inputValue)
            }
            placeholder="Add Start Address"
            isSearchable
            className="mb-1"
            styles={selectStyles(!!errors.startLocation)}
          />
          <ErrorMessage message={errors.startLocation} />
        </div>

        {/* End Location (Walmart) */}
        <div className="mt-4">
          <label className="block mb-1 font-medium text-gray-700">
            Destination (Walmart)*
          </label>
          <CreatableSelect
            options={locations.map((loc) => ({
              value: loc,
              label: `${loc.city}, ${loc.state}`,
            }))}
            onChange={handleEndLocationChange}
            onCreateOption={(inputValue: string) =>
              handleCreateWalmartOption(inputValue)
            }
            placeholder="Add Destination Address"
            isSearchable
            className="mb-1"
            styles={selectStyles(!!errors.endLocation)}
          />
          <ErrorMessage message={errors.endLocation} />
        </div>

        {/* Buttons */}
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded"
          >
            Add Route
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRoute;
